import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Printer, Trophy, Check } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";

// ------------------ Kas Wadah Dialog ------------------
const KAS_KATEGORI = ["UMUM", "IBADAH", "UNDIAN", "SUMBANGAN", "PROGRAM"];
export function KasDialog({ open, onClose, wadah, onDone }) {
  const [form, setForm] = useState({ tanggal: new Date().toISOString().slice(0,10), jenis: "MASUK", kategori: "IBADAH", uraian: "", jumlah: "", keterangan: "" });
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) setForm({ tanggal: new Date().toISOString().slice(0,10), jenis: "MASUK", kategori: "IBADAH", uraian: "", jumlah: "", keterangan: "" }); }, [open]);
  const submit = async () => {
    if (!form.uraian || !form.jumlah) { toast.error("Uraian & jumlah wajib"); return; }
    setSaving(true);
    try {
      await api.post("/wadah-kas", { ...form, wadah: wadah.code, jumlah: Number(form.jumlah) });
      toast.success("Transaksi tercatat");
      onClose(); onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Catat Kas {wadah.title}</DialogTitle>
          <DialogDescription>Buku kas terpisah dari Buku Kas Klasis. Digunakan Bendahara Wadah untuk rekap ibadah.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label>Tanggal</Label>
              <Input type="date" value={form.tanggal} onChange={(e)=>setForm({...form, tanggal:e.target.value})} data-testid="input-kas-tanggal" />
            </div>
            <div>
              <Label>Jenis</Label>
              <Select value={form.jenis} onValueChange={(v)=>setForm({...form, jenis:v})}>
                <SelectTrigger data-testid="select-kas-jenis"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="MASUK">Kas Masuk</SelectItem>
                  <SelectItem value="KELUAR">Kas Keluar</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Kategori</Label>
              <Select value={form.kategori} onValueChange={(v)=>setForm({...form, kategori:v})}>
                <SelectTrigger data-testid="select-kas-kategori"><SelectValue /></SelectTrigger>
                <SelectContent>{KAS_KATEGORI.map(k => <SelectItem key={k} value={k}>{k}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label>Uraian</Label>
            <Input value={form.uraian} onChange={(e)=>setForm({...form, uraian:e.target.value})} placeholder="Contoh: Persembahan Ibadah PERIA di El Olam" data-testid="input-kas-uraian" />
          </div>
          <div>
            <Label>Jumlah (Rp)</Label>
            <Input type="number" value={form.jumlah} onChange={(e)=>setForm({...form, jumlah:e.target.value})} placeholder="0" data-testid="input-kas-jumlah" />
          </div>
          <div>
            <Label>Keterangan (opsional)</Label>
            <Textarea rows={2} value={form.keterangan} onChange={(e)=>setForm({...form, keterangan:e.target.value})} data-testid="input-kas-keterangan" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Batal</Button>
          <Button onClick={submit} disabled={saving} data-testid="btn-save-kas" style={{backgroundColor: wadah.accent}}>{saving ? "Menyimpan…" : "Simpan Kas"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ------------------ Undian Baru Dialog ------------------
export function UndianDialog({ open, onClose, wadah, onDone }) {
  const [form, setForm] = useState({ title: "", event_date: new Date().toISOString().slice(0,10), location: "", hadiah: "", penyedia_hadiah: "", coupons_count: 30 });
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) setForm({ title: `Undian Ibadah ${wadah.title} — ${new Date().toLocaleDateString('id-ID',{month:'long',year:'numeric'})}`, event_date: new Date().toISOString().slice(0,10), location: "", hadiah: "", penyedia_hadiah: "", coupons_count: 30 }); }, [open, wadah]);
  const submit = async () => {
    if (!form.title || !form.event_date) { toast.error("Judul & tanggal wajib"); return; }
    setSaving(true);
    try {
      await api.post("/undian-events", { ...form, wadah: wadah.code, coupons_count: Number(form.coupons_count) });
      toast.success(`Event undian dibuat + ${form.coupons_count} kupon QR`);
      onClose(); onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Undian Baru {wadah.title}</DialogTitle>
          <DialogDescription>Buat event undian & auto-generate kupon dengan QR unik yang bisa dicetak/dibagikan.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Judul Undian</Label>
            <Input value={form.title} onChange={(e)=>setForm({...form, title:e.target.value})} data-testid="input-undian-title" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Tanggal Undian</Label>
              <Input type="date" value={form.event_date} onChange={(e)=>setForm({...form, event_date:e.target.value})} data-testid="input-undian-date" />
            </div>
            <div>
              <Label>Jumlah Kupon</Label>
              <Input type="number" min="1" max="200" value={form.coupons_count} onChange={(e)=>setForm({...form, coupons_count:e.target.value})} data-testid="input-undian-count" />
            </div>
          </div>
          <div>
            <Label>Lokasi Ibadah</Label>
            <Input value={form.location} onChange={(e)=>setForm({...form, location:e.target.value})} placeholder="GPDP Jemaat …" data-testid="input-undian-location" />
          </div>
          <div>
            <Label>Hadiah Undian</Label>
            <Input value={form.hadiah} onChange={(e)=>setForm({...form, hadiah:e.target.value})} placeholder="Beras 5kg + Sarden" data-testid="input-undian-hadiah" />
          </div>
          <div>
            <Label>Penyedia Hadiah (Pemenang Sebelumnya)</Label>
            <Input value={form.penyedia_hadiah} onChange={(e)=>setForm({...form, penyedia_hadiah:e.target.value})} data-testid="input-undian-penyedia" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Batal</Button>
          <Button onClick={submit} disabled={saving} data-testid="btn-save-undian" style={{backgroundColor: wadah.accent}}>{saving ? "Menyimpan…" : "Buat Event + Kupon"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ------------------ Undian Event Detail: Coupon Sheet + Winner ------------------
export function UndianEventDetail({ eventId, wadah, onClose, onDone }) {
  const [ev, setEv] = useState(null);
  const [loading, setLoading] = useState(true);
  const [assignOpen, setAssignOpen] = useState(null); // coupon obj
  const [name, setName] = useState("");

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get(`/undian-events/${eventId}`); setEv(data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [eventId]);

  const redeem = async () => {
    if (!name.trim()) { toast.error("Nama wajib"); return; }
    try {
      await api.patch(`/undian-coupons/${assignOpen.id}/redeem`, { anggota_name: name.trim() });
      toast.success(`Kupon ${assignOpen.code.slice(-6)} diberikan ke ${name}`);
      setAssignOpen(null); setName("");
      load(); onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const setWinner = async (couponId) => {
    if (!window.confirm("Tetapkan sebagai pemenang undian? Ini akan menutup event.")) return;
    try {
      await api.patch(`/undian-events/${eventId}/winner/${couponId}`);
      toast.success("Pemenang ditetapkan");
      load(); onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const printSheet = () => window.print();

  if (!ev && !loading) return null;

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-5xl max-h-[92vh] overflow-y-auto p-0 gap-0">
        <div className="flex items-center justify-between p-3 border-b border-border print:hidden bg-muted/20">
          <div className="text-xs text-muted-foreground">{ev?.title}</div>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" className="rounded-full" onClick={printSheet} data-testid="btn-print-coupons">
              <Printer className="h-3.5 w-3.5 mr-1.5" /> Cetak Lembar Kupon
            </Button>
          </div>
        </div>

        <div className="surat-print-root px-6 py-6 bg-white" data-testid="coupon-sheet">
          {/* Header */}
          <div className="mb-4 flex items-center gap-3 pb-4 border-b-2" style={{borderColor: wadah.accent}}>
            <img src="/logo-gpdp.png" alt="Logo GPDP" className="h-12 w-12 object-contain" />
            <div>
              <div className="text-[0.65rem] tracking-[0.25em] uppercase font-bold" style={{color: wadah.accent}}>Kupon Undian Berkat — {wadah.title}</div>
              <div className="font-heading font-black text-lg leading-tight">{ev?.title}</div>
              <div className="text-xs text-muted-foreground">{ev && new Date(ev.event_date).toLocaleDateString("id-ID", {day:"numeric", month:"long", year:"numeric"})} · {ev?.location}</div>
              {ev?.hadiah && <div className="text-xs mt-0.5"><strong>Hadiah:</strong> {ev.hadiah}</div>}
            </div>
          </div>

          {loading ? (
            <div className="text-center py-12 text-sm text-muted-foreground">Memuat kupon…</div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
              {(ev?.coupons || []).map((c) => {
                const redeemed = !!c.redeemed_at;
                const isWinner = !!c.is_winner;
                return (
                  <div
                    key={c.id}
                    className={`border-2 rounded-lg p-3 text-center relative overflow-hidden ${isWinner ? 'ring-4' : ''}`}
                    style={{
                      borderColor: isWinner ? wadah.accent : (redeemed ? '#d1d5db' : wadah.accent),
                      borderStyle: redeemed && !isWinner ? 'dashed' : 'solid',
                      backgroundColor: isWinner ? `${wadah.accent}18` : (redeemed ? '#f9fafb' : '#ffffff'),
                    }}
                    data-testid={`coupon-${c.id}`}
                  >
                    {isWinner && (
                      <div className="absolute top-1 right-1 rounded-full p-1 text-white" style={{backgroundColor: wadah.accent}}>
                        <Trophy className="h-3 w-3" />
                      </div>
                    )}
                    <div className="text-[0.6rem] uppercase tracking-widest font-bold" style={{color: wadah.accent}}>Kupon #{String(c.coupon_number).padStart(3, '0')}</div>
                    <div className="mt-2 mx-auto w-20 h-20 flex items-center justify-center bg-white">
                      <QRCodeSVG value={c.code} size={72} level="M" />
                    </div>
                    <div className="mt-2 font-mono text-[0.55rem] break-all font-semibold">{c.code}</div>
                    {c.anggota_name ? (
                      <div className="mt-1 text-[0.65rem] font-bold" style={{color: wadah.accent}}>{c.anggota_name}</div>
                    ) : (
                      <div className="mt-1 text-[0.55rem] text-muted-foreground italic">— Kosong —</div>
                    )}
                    <div className="mt-2 flex items-center justify-center gap-1 print:hidden">
                      {!redeemed && (
                        <button onClick={() => setAssignOpen(c)} className="text-[0.6rem] px-2 py-0.5 rounded-full border hover:bg-muted" data-testid={`btn-assign-coupon-${c.coupon_number}`}>Isi Nama</button>
                      )}
                      {redeemed && !isWinner && ev.status === "OPEN" && (
                        <button onClick={() => setWinner(c.id)} className="text-[0.6rem] px-2 py-0.5 rounded-full text-white" style={{backgroundColor: wadah.accent}} data-testid={`btn-winner-${c.coupon_number}`}>Menang</button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Aturan main small print */}
          <div className="mt-6 pt-4 border-t border-border text-[0.65rem] text-muted-foreground print:mt-3">
            <strong>Aturan Undian Berkat:</strong> Setiap anggota berhak 1 kupon. Kupon dipindai QR-nya sebelum ibadah. Pemenang menyediakan hadiah undian pada ibadah {wadah.title} berikutnya. Info: SIMPELKAMI GPDP.
          </div>
        </div>

        {/* Assign Name Dialog */}
        {assignOpen && (
          <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4 print:hidden" onClick={() => { setAssignOpen(null); setName(""); }}>
            <div className="bg-background rounded-lg p-5 max-w-sm w-full shadow-xl" onClick={(e)=>e.stopPropagation()}>
              <div className="font-heading font-bold text-sm">Isi Nama Peserta</div>
              <div className="text-xs text-muted-foreground mt-1">Kupon #{String(assignOpen.coupon_number).padStart(3,'0')} — {assignOpen.code}</div>
              <Input
                value={name}
                onChange={(e)=>setName(e.target.value)}
                placeholder="Nama anggota…"
                className="mt-3"
                autoFocus
                data-testid="input-coupon-name"
                onKeyDown={(e) => e.key === "Enter" && redeem()}
              />
              <div className="flex justify-end gap-2 mt-3">
                <Button size="sm" variant="outline" onClick={() => { setAssignOpen(null); setName(""); }}>Batal</Button>
                <Button size="sm" onClick={redeem} style={{backgroundColor: wadah.accent}} data-testid="btn-confirm-coupon-name"><Check className="h-3.5 w-3.5 mr-1" />Simpan</Button>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
